import {FullStory, init} from '@fullstory/browser';

/*
 * Documentation: https://developer.fullstory.com/browser/getting-started/
 */

const fullStoryEnabled = import.meta.env.VITE_FULLSTORY_ENABLED === 'true';
const fullStoryOrgId = import.meta.env.VITE_FULLSTORY_ORG_ID;

const NumericLogLevel = {
  log: 0, // Clamps to Debug on iOS
  debug: 1,
  info: 2, // Default
  warn: 3,
  error: 4,
  assert: 5, // Clamps to Error on Android
} as const;

class FullStoryService {
  private readonly enabled = fullStoryEnabled;
  private readonly FS: ((operation: string, options?: Record<string, unknown>) => void) | null = null;

  constructor() {
    if (this.enabled) {
      if (!fullStoryOrgId) {
        console.warn('FullStory is enabled but Org ID is missing');
        return;
      }
      console.log('FullStory is enabled with org ID:', fullStoryOrgId);
      init({
        orgId: fullStoryOrgId as string,
      });
      this.FS = (window as any).FS;
    }
  }

  identify = async (userId: string, properties?: Record<string, unknown>) => {
    try {
      this.FS?.('setIdentity', {
        uid: userId,
        properties, // If possible, provide `displayName` and `email` which are used in Fullstory to label data
      });
    } catch (error) {
      console.error('Failed to set user ID in FullStory:', error);
    }
  };

  setUserProperties = (properties: Record<string, unknown>) => {
    this.FS?.('setProperties', {type: 'user', properties});
  };

  anonymizeUser = () => {
    this.FS?.('setIdentity', {anonymous: true});
  };

  useConsent = (consent: boolean) => {
    this.FS?.('setIdentity', {consent});
  };

  logScreenView = async (screenName: string, properties?: Record<string, unknown>) => {
    try {
      this.FS?.('trackEvent', {
        name: 'screen_view',
        properties: {
          ...properties,
          pageName: screenName,
        },
      });
    } catch (error) {
      console.error('Failed to track screen view in FullStory:', error);
    }
  };

  logCustomEvent = async (eventName: string, properties: Record<string, unknown>) => {
    try {
      this.FS?.('trackEvent', {
        name: eventName,
        properties,
      });
    } catch (error) {
      console.error(`Failed to log FullStory custom event ${eventName}:`, error);
    }
  };

  logMessage = (message: string, level: 'log' | 'info' | 'warn' | 'error' = 'log') => {
    this.FS?.('log', {message, level});
  };

  getFullStoryConfig() {
    return FullStory;
  }
}

export const fullStoryService = new FullStoryService();
